import { ReactNode, useEffect, useRef, useState } from 'react';
import { AutoComplete, Button, InputGroup } from 'rsuite';
import { useGetRegionsQuery } from '@monorepo/shared/services/globalApi';
import CloseIcon from '@rsuite/icons/Close';
import { getCoords } from '../utils/helper';
import { useAppSelector } from '../store/hook';
import {
  selectAllowProvisionRegions,
  selectGlobalToken,
  selectRegions
} from '../store/authSlice';
import { MdOutlineDisabledByDefault } from 'react-icons/md';
import SpinnerIcon from '@rsuite/icons/Spinner';
import './RegionSelector.scss';
import { useLazyRegionLoginQuery } from '../services';

type AutoCompleteItem = Record<string, any>;

interface IProps {
  onSelectHandle?: (obj?: AutoCompleteItem) => void;
  resetNum?: number;
  server: string;
  enableTimezone?: boolean;
  onRegionLoginErrorHandle?: (region: Record<string, any>) => void;
}
export const RegionSelector = ({
  onSelectHandle,
  onRegionLoginErrorHandle,
  server,
  resetNum,
  enableTimezone
}: IProps) => {
  const [regionLogin, { isError, isLoading, isFetching, originalArgs }] =
    useLazyRegionLoginQuery();
  const globalToken = useAppSelector(selectGlobalToken);
  const allowProvisionRegions = useAppSelector(selectAllowProvisionRegions);
  const authRegions = useAppSelector(selectRegions);
  const [regions, setRegions] = useState<AutoCompleteItem[]>([]);
  const [value, setValue] = useState<string>('');
  const menuRef = useRef<any>(null);
  const inputRef = useRef<any>(null);
  const requestRef = useRef<any>(null);
  const {
    data: regionsData,
    error: regionsError,
    isLoading: regionsIsLoading
  } = useGetRegionsQuery(
    { server: server },
    {
      skip: !globalToken
    }
  );
  const relocate = () => {
    if (menuRef.current && inputRef.current) {
      requestRef.current = requestAnimationFrame(() => {
        const menuWrapper = menuRef.current.parentElement;
        const position = getCoords(inputRef.current);
        menuWrapper.style.top = position.top + 35 + 'px';
      });
    }
  };
  useEffect(() => {
    window.addEventListener('wheel', relocate);

    return () => {
      window.removeEventListener('wheel', relocate);
      cancelAnimationFrame(requestRef.current);
    };
  }, []);
  useEffect(() => {
    if (regionsData?.length) {
      const filteredRegions = allowProvisionRegions?.length
        ? regionsData.filter((obj: Record<string, any>) =>
            allowProvisionRegions?.includes(obj.alias)
          )
        : regionsData;
      const defaultRegions: AutoCompleteItem[] = filteredRegions.map(
        (region) =>
          ({
            label: region.name,
            value: region.name,
            rawValue: region.alias,
            isDefault: region.is_default,
            enableImageVersion:
              authRegions[region.alias]?.image_versions?.length > 1,
            enableAdomVersion: authRegions[region.alias]?.enable_adom_version
          }) as AutoCompleteItem
      );
      setRegions(defaultRegions);
      const defaultRegion = filteredRegions.find(
        (obj) => obj?.is_default
        // !Object.keys(rejectedRegions).includes(obj?.alias) &&
        // authRegions[obj.alias]?.token
      );

      if (!value && defaultRegion) {
        setValue(defaultRegion.name);
        const defaultRegionData = defaultRegions.find(
          (obj) => obj?.rawValue === defaultRegion.alias
        );
        onSelectHandle && onSelectHandle(defaultRegionData);
      } else {
        onSelectHandle && onSelectHandle({});
      }
    }
  }, [regionsData, authRegions]);

  useEffect(() => {
    if (value) {
      const selectedData = regions.find((obj) => obj?.value === value);
      onSelectHandle && onSelectHandle(selectedData);
      const selectedRegion = selectedData?.rawValue
        ? authRegions[selectedData.rawValue]
        : undefined;
      selectedRegion?.globalToken &&
        !selectedRegion?.token &&
        regionLogin({ region: selectedRegion, enableTimezone });
    }
  }, [value, regions]);

  useEffect(() => {
    if (isError && originalArgs?.region) {
      onRegionLoginErrorHandle && onRegionLoginErrorHandle(originalArgs.region);
    }
  }, [isError, isLoading, isFetching, originalArgs]);

  const resetHandle = () => {
    const defaultOpt = regions?.find((obj) => obj?.isDefault);
    if (defaultOpt) {
      setValue(`${defaultOpt.value}`);
      onSelectHandle && onSelectHandle(defaultOpt);
    }
  };

  useEffect(() => {
    if (resetNum) {
      resetHandle();
    }
  }, [resetNum]);

  const styles = {
    width: '100%',
    minWidth: '300px',
    display: 'inline-flex'
  };
  const renderMenu = (menu: any) => {
    return (
      <div ref={menuRef} className="region-selector">
        {menu}
      </div>
    );
  };
  const renderMenuItem = (label: ReactNode, item: AutoCompleteItem) => {
    return (
      <Button
        appearance="link"
        style={{
          width: '100%',
          justifyContent: 'flex-start',
          color: item?.disabled ? 'red' : item?.onLogin ? '#204dd4' : 'unset'
        }}
        disabled={item.disabled || item.onLogin}
        title={
          item.disabled
            ? `Region ${item?.label} login failed, please try later.`
            : item.onLogin
              ? `Region ${item?.label} is logging in, please try later`
              : ''
        }
      >
        {label}
        {item?.disabled && (
          <MdOutlineDisabledByDefault
            color="red"
            style={{ marginLeft: '16px' }}
          />
        )}
        {item?.onLogin && (
          <SpinnerIcon color="#204dd4" style={{ marginLeft: '16px' }} />
        )}
      </Button>
    );
  };

  const filterBy = (value: string, item: AutoCompleteItem) => {
    if (!value.trim() || regions.some((obj) => obj?.label === value)) {
      return true;
    }
    const keyword = (value || '').toLocaleLowerCase();
    return `${item.label}`.toLocaleLowerCase().indexOf(keyword) >= 0;
  };
  const onSelect = (value: any) => {
    const selectedItem = regions.find((obj) => obj?.value === value);
    onSelectHandle?.(selectedItem);
  };
  return (
    <InputGroup inside style={styles} ref={inputRef}>
      <AutoComplete
        value={value}
        data={regions}
        filterBy={filterBy}
        onChange={(value: string) => {
          setValue(value);
        }}
        onSelect={onSelect}
      />
      {value && (
        <InputGroup.Button
          tabIndex={-1}
          onClick={() => {
            setValue('');
            onSelectHandle && onSelectHandle({});
          }}
        >
          <CloseIcon />
        </InputGroup.Button>
      )}
    </InputGroup>
  );
};
